import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format as formatDateFns } from 'date-fns';
import {
    StatementData,
    StatementFormat,
    TransactionRow,
    TradeRow,
    StatementSummary,
} from '../types/statement';

// Helper function to format currency
function formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
    }).format(amount);
}

// Helper function to format date
function formatDate(date: Date): string {
    return formatDateFns(date, 'yyyy-MM-dd HH:mm:ss');
}

// Aggregate statement data from transactions and trades
export function aggregateStatementData(
    transactions: any[],
    trades: any[],
    customerProfile: any
): StatementData {
    // Filter only completed transactions
    const completedTransactions = transactions.filter(
        t => t.status === 'completed' || t.status === 'approved'
    );

    // Combine transactions and trades into timeline
    const timeline: Array<{ date: Date; type: 'transaction' | 'trade'; data: any }> = [
        ...completedTransactions.map(t => ({
            date: new Date(t.processed_at || t.created_at),
            type: 'transaction' as const,
            data: t,
        })),
        ...trades.map(t => ({
            date: new Date(t.closed_at),
            type: 'trade' as const,
            data: t,
        })),
    ];

    // Sort chronologically
    timeline.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Calculate running balances and create transaction rows
    let balance = 0;
    const transactionRows: TransactionRow[] = [];

    // Initialize opening balance from first transaction
    if (completedTransactions.length > 0) {
        const firstTxn = completedTransactions.sort(
            (a, b) =>
                new Date(a.processed_at || a.created_at).getTime() -
                new Date(b.processed_at || b.created_at).getTime()
        )[0];
        if (firstTxn.balance_before != null) {
            balance = Number(firstTxn.balance_before);
        }
    }

    const openingBalance = balance;

    // Process timeline items
    for (const item of timeline) {
        if (item.type === 'transaction') {
            const txn = item.data;
            const amount = Number(txn.amount || 0);

            // Apply transaction to balance
            if (txn.type === 'deposit' || txn.type === 'bonus' || txn.type === 'credit') {
                balance += amount;
            } else if (txn.type === 'withdrawal' || txn.type === 'debit') {
                balance -= amount;
            }

            transactionRows.push({
                date: item.date,
                type: txn.type?.toUpperCase() || 'TRANSACTION',
                reference: txn.reference_number || txn.reference_id || 'N/A',
                description: txn.description || txn.type?.toUpperCase() || '',
                amount: amount,
                balance: balance,
            });
        } else if (item.type === 'trade') {
            const trade = item.data;
            const pnl = Number(trade.realized_pnl || 0);

            // Apply trade P&L to balance
            balance += pnl;

            transactionRows.push({
                date: item.date,
                type: `TRADE P&L`,
                reference: trade.id?.substring(0, 8) || 'N/A',
                description: `${trade.side?.toUpperCase()} ${trade.instrument?.symbol || 'N/A'} (${Number(trade.lot_size || 0).toFixed(2)} lots)`,
                amount: pnl,
                balance: balance,
            });
        }
    }

    // Create trade rows
    const tradeRows: TradeRow[] = trades.map(trade => ({
        dateOpened: new Date(trade.opened_at),
        dateClosed: new Date(trade.closed_at),
        symbol: trade.instrument?.symbol || 'N/A',
        side: trade.side === 'buy' ? 'buy' : 'sell',
        lots: Number(trade.lot_size || 0),
        entryPrice: Number(trade.entry_price || 0),
        exitPrice: Number(trade.exit_price || 0),
        pnl: Number(trade.realized_pnl || 0),
        commission: Number(trade.commission || 0),
    }));

    // Calculate summary totals
    const totalDeposits = completedTransactions
        .filter(t => t.type === 'deposit')
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

    const totalWithdrawals = completedTransactions
        .filter(t => t.type === 'withdrawal')
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

    const totalBonuses = completedTransactions
        .filter(t => t.type === 'bonus')
        .reduce((sum, t) => sum + Number(t.amount || 0), 0);

    const tradingPnL = trades.reduce((sum, t) => sum + Number(t.realized_pnl || 0), 0);

    const closingBalance = balance;

    // Determine period
    const dates = timeline.map(t => t.date);
    const fromDate = dates.length > 0 ? new Date(Math.min(...dates.map(d => d.getTime()))) : null;
    const toDate = new Date();

    // Get customer info
    const customerNumber =
        customerProfile?.customer_number ||
        (customerProfile?.id ? `CUST-${customerProfile.id.substring(0, 8)}` : 'N/A');

    const summary: StatementSummary = {
        openingBalance,
        totalDeposits,
        totalWithdrawals,
        totalBonuses,
        tradingPnL,
        closingBalance,
        currency: customerProfile?.base_currency || 'USD',
    };

    return {
        customer: {
            name: customerProfile?.name || customerProfile?.full_name || 'N/A',
            customer_number: customerNumber,
            email: customerProfile?.email || 'N/A',
        },
        period: {
            from: fromDate,
            to: toDate,
        },
        summary,
        transactions: transactionRows,
        trades: tradeRows,
        generatedAt: new Date(),
    };
}

// Generate PDF statement
function generatePDF(data: StatementData): Blob {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let yPos = 20;

    // Header
    doc.setFontSize(20);
    doc.text('Account Statement', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Customer info
    doc.setFontSize(10);
    doc.text(`Customer: ${data.customer.name}`, 14, yPos);
    yPos += 6;
    doc.text(`Customer Number: ${data.customer.customer_number}`, 14, yPos);
    yPos += 6;
    doc.text(`Email: ${data.customer.email}`, 14, yPos);
    yPos += 6;
    doc.text(
        `Period: ${data.period.from ? formatDate(data.period.from) : 'All Time'} to ${formatDate(data.period.to)}`,
        14,
        yPos
    );
    yPos += 6;
    doc.text(`Generated: ${formatDate(data.generatedAt)}`, 14, yPos);
    yPos += 10;

    // Summary section
    doc.setFontSize(14);
    doc.text('Account Summary', 14, yPos);
    yPos += 8;

    autoTable(doc, {
        startY: yPos,
        head: [['Description', 'Amount']],
        body: [
            ['Opening Balance', formatCurrency(data.summary.openingBalance, data.summary.currency)],
            ['Total Deposits', formatCurrency(data.summary.totalDeposits, data.summary.currency)],
            [
                'Total Withdrawals',
                formatCurrency(data.summary.totalWithdrawals, data.summary.currency),
            ],
            ['Total Bonuses', formatCurrency(data.summary.totalBonuses, data.summary.currency)],
            ['Trading P&L', formatCurrency(data.summary.tradingPnL, data.summary.currency)],
            ['Closing Balance', formatCurrency(data.summary.closingBalance, data.summary.currency)],
        ],
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202] },
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;

    // Transactions table
    if (data.transactions.length > 0) {
        doc.setFontSize(14);
        doc.text('Transaction History', 14, yPos);
        yPos += 8;

        autoTable(doc, {
            startY: yPos,
            head: [['Date', 'Type', 'Reference', 'Description', 'Amount', 'Balance']],
            body: data.transactions.map(t => [
                formatDate(t.date),
                t.type,
                t.reference,
                t.description,
                formatCurrency(t.amount, data.summary.currency),
                formatCurrency(t.balance, data.summary.currency),
            ]),
            theme: 'striped',
            headStyles: { fillColor: [66, 139, 202] },
            styles: { fontSize: 8 },
        });

        yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Trades table
    if (data.trades.length > 0) {
        // Check if we need a new page
        if (yPos > 250) {
            doc.addPage();
            yPos = 20;
        }

        doc.setFontSize(14);
        doc.text('Closed Trades', 14, yPos);
        yPos += 8;

        autoTable(doc, {
            startY: yPos,
            head: [
                [
                    'Opened',
                    'Closed',
                    'Symbol',
                    'Side',
                    'Lots',
                    'Entry',
                    'Exit',
                    'P&L',
                    'Commission',
                ],
            ],
            body: data.trades.map(t => [
                formatDate(t.dateOpened),
                formatDate(t.dateClosed),
                t.symbol,
                t.side.toUpperCase(),
                t.lots.toFixed(2),
                t.entryPrice.toFixed(5),
                t.exitPrice.toFixed(5),
                formatCurrency(t.pnl, data.summary.currency),
                formatCurrency(t.commission, data.summary.currency),
            ]),
            theme: 'striped',
            headStyles: { fillColor: [66, 139, 202] },
            styles: { fontSize: 7 },
        });
    }

    // Footer
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, doc.internal.pageSize.height - 10, {
            align: 'center',
        });
    }

    return doc.output('blob');
}

// Generate Excel statement
function generateExcel(data: StatementData): Blob {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Summary
    const summaryData = [
        ['ACCOUNT STATEMENT'],
        [''],
        ['Customer Information'],
        ['Name:', data.customer.name],
        ['Customer Number:', data.customer.customer_number],
        ['Email:', data.customer.email],
        [''],
        [
            'Period:',
            `${data.period.from ? formatDate(data.period.from) : 'All Time'} to ${formatDate(data.period.to)}`,
        ],
        ['Generated:', formatDate(data.generatedAt)],
        [''],
        ['Account Summary'],
        ['Description', 'Amount'],
        ['Opening Balance', data.summary.openingBalance],
        ['Total Deposits', data.summary.totalDeposits],
        ['Total Withdrawals', data.summary.totalWithdrawals],
        ['Total Bonuses', data.summary.totalBonuses],
        ['Trading P&L', data.summary.tradingPnL],
        ['Closing Balance', data.summary.closingBalance],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

    // Set column widths
    summarySheet['!cols'] = [{ wch: 20 }, { wch: 30 }];

    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Sheet 2: Transactions
    if (data.transactions.length > 0) {
        const transactionsData = [
            ['Date', 'Type', 'Reference', 'Description', 'Amount', 'Balance'],
            ...data.transactions.map(t => [
                formatDate(t.date),
                t.type,
                t.reference,
                t.description,
                t.amount,
                t.balance,
            ]),
        ];

        const transactionsSheet = XLSX.utils.aoa_to_sheet(transactionsData);
        transactionsSheet['!cols'] = [
            { wch: 20 },
            { wch: 15 },
            { wch: 15 },
            { wch: 30 },
            { wch: 15 },
            { wch: 15 },
        ];

        XLSX.utils.book_append_sheet(workbook, transactionsSheet, 'Transactions');
    }

    // Sheet 3: Closed Trades
    if (data.trades.length > 0) {
        const tradesData = [
            [
                'Date Opened',
                'Date Closed',
                'Symbol',
                'Side',
                'Lots',
                'Entry Price',
                'Exit Price',
                'P&L',
                'Commission',
            ],
            ...data.trades.map(t => [
                formatDate(t.dateOpened),
                formatDate(t.dateClosed),
                t.symbol,
                t.side.toUpperCase(),
                t.lots,
                t.entryPrice,
                t.exitPrice,
                t.pnl,
                t.commission,
            ]),
        ];

        const tradesSheet = XLSX.utils.aoa_to_sheet(tradesData);
        tradesSheet['!cols'] = [
            { wch: 20 },
            { wch: 20 },
            { wch: 12 },
            { wch: 8 },
            { wch: 10 },
            { wch: 12 },
            { wch: 12 },
            { wch: 15 },
            { wch: 12 },
        ];

        XLSX.utils.book_append_sheet(workbook, tradesSheet, 'Closed Trades');
    }

    // Generate binary string
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
}

// Generate HTML statement
function generateHTML(data: StatementData): Blob {
    const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Account Statement - ${data.customer.customer_number}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 20px;
            color: #333;
        }
        h1 {
            text-align: center;
            color: #2c3e50;
        }
        .customer-info {
            margin: 20px 0;
            padding: 15px;
            background-color: #f8f9fa;
            border-radius: 5px;
        }
        .customer-info p {
            margin: 5px 0;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
        }
        th, td {
            padding: 10px;
            text-align: left;
            border-bottom: 1px solid #ddd;
        }
        th {
            background-color: #428bca;
            color: white;
        }
        tr:nth-child(even) {
            background-color: #f2f2f2;
        }
        .summary-table td:first-child {
            font-weight: bold;
        }
        .section-title {
            margin-top: 30px;
            color: #2c3e50;
            border-bottom: 2px solid #428bca;
            padding-bottom: 5px;
        }
        .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            color: #666;
        }
        @media print {
            body {
                margin: 0;
            }
            .no-print {
                display: none;
            }
        }
    </style>
</head>
<body>
    <h1>Account Statement</h1>

    <div class="customer-info">
        <p><strong>Customer:</strong> ${data.customer.name}</p>
        <p><strong>Customer Number:</strong> ${data.customer.customer_number}</p>
        <p><strong>Email:</strong> ${data.customer.email}</p>
        <p><strong>Period:</strong> ${data.period.from ? formatDate(data.period.from) : 'All Time'} to ${formatDate(data.period.to)}</p>
        <p><strong>Generated:</strong> ${formatDate(data.generatedAt)}</p>
    </div>

    <h2 class="section-title">Account Summary</h2>
    <table class="summary-table">
        <tr>
            <td>Opening Balance</td>
            <td>${formatCurrency(data.summary.openingBalance, data.summary.currency)}</td>
        </tr>
        <tr>
            <td>Total Deposits</td>
            <td>${formatCurrency(data.summary.totalDeposits, data.summary.currency)}</td>
        </tr>
        <tr>
            <td>Total Withdrawals</td>
            <td>${formatCurrency(data.summary.totalWithdrawals, data.summary.currency)}</td>
        </tr>
        <tr>
            <td>Total Bonuses</td>
            <td>${formatCurrency(data.summary.totalBonuses, data.summary.currency)}</td>
        </tr>
        <tr>
            <td>Trading P&L</td>
            <td>${formatCurrency(data.summary.tradingPnL, data.summary.currency)}</td>
        </tr>
        <tr style="background-color: #e7f3ff;">
            <td><strong>Closing Balance</strong></td>
            <td><strong>${formatCurrency(data.summary.closingBalance, data.summary.currency)}</strong></td>
        </tr>
    </table>

    ${
        data.transactions.length > 0
            ? `
    <h2 class="section-title">Transaction History</h2>
    <table>
        <thead>
            <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Reference</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Balance</th>
            </tr>
        </thead>
        <tbody>
            ${data.transactions
                .map(
                    t => `
                <tr>
                    <td>${formatDate(t.date)}</td>
                    <td>${t.type}</td>
                    <td>${t.reference}</td>
                    <td>${t.description}</td>
                    <td>${formatCurrency(t.amount, data.summary.currency)}</td>
                    <td>${formatCurrency(t.balance, data.summary.currency)}</td>
                </tr>
            `
                )
                .join('')}
        </tbody>
    </table>
    `
            : ''
    }

    ${
        data.trades.length > 0
            ? `
    <h2 class="section-title">Closed Trades</h2>
    <table>
        <thead>
            <tr>
                <th>Opened</th>
                <th>Closed</th>
                <th>Symbol</th>
                <th>Side</th>
                <th>Lots</th>
                <th>Entry Price</th>
                <th>Exit Price</th>
                <th>P&L</th>
                <th>Commission</th>
            </tr>
        </thead>
        <tbody>
            ${data.trades
                .map(
                    t => `
                <tr>
                    <td>${formatDate(t.dateOpened)}</td>
                    <td>${formatDate(t.dateClosed)}</td>
                    <td>${t.symbol}</td>
                    <td>${t.side.toUpperCase()}</td>
                    <td>${t.lots.toFixed(2)}</td>
                    <td>${t.entryPrice.toFixed(5)}</td>
                    <td>${t.exitPrice.toFixed(5)}</td>
                    <td>${formatCurrency(t.pnl, data.summary.currency)}</td>
                    <td>${formatCurrency(t.commission, data.summary.currency)}</td>
                </tr>
            `
                )
                .join('')}
        </tbody>
    </table>
    `
            : ''
    }

    <div class="footer">
        <p>This statement was automatically generated on ${formatDate(data.generatedAt)}</p>
        <p>For questions or concerns, please contact support.</p>
    </div>

    <div class="no-print" style="margin-top: 20px; text-align: center;">
        <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">Print Statement</button>
    </div>
</body>
</html>
    `;

    return new Blob([html], { type: 'text/html' });
}

// Main export function
export async function downloadStatement(
    format: StatementFormat,
    transactions: any[],
    trades: any[],
    customerProfile: any
): Promise<void> {
    // Aggregate data
    const statementData = aggregateStatementData(transactions, trades, customerProfile);

    // Generate format-specific blob
    let blob: Blob;
    let extension: string;

    switch (format) {
        case 'pdf':
            blob = generatePDF(statementData);
            extension = 'pdf';
            break;
        case 'excel':
            blob = generateExcel(statementData);
            extension = 'xlsx';
            break;
        case 'html':
            blob = generateHTML(statementData);
            extension = 'html';
            break;
        default:
            throw new Error(`Unsupported format: ${format}`);
    }

    // Create download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const date = formatDateFns(new Date(), 'yyyy-MM-dd');
    const filename = `account-statement-${statementData.customer.customer_number}-${date}.${extension}`;
    link.download = filename;

    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
}
